import React, { useEffect, useState } from "react";
import { Spinner } from "@amzn/awsui-components-react-v3";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { AdminDetailsObject } from "../adminDetailsObject";
import InfoLink from "../../common/infoLink/InfoLink";
import styles from "../createTeam.module.css";
import { InheritedAccessDetails } from "../inheritedAccessDetails";
import helpPanelSlice, {
  DisplayHelpPanel,
} from "../../help_panel/helpPanelSlice";
import { LoadingState, useHttpRequest } from "../../http";
import { MetricsState } from "../../logging";
import { InheritedAccessModal } from "./inheritedAccessModal";

interface InheritedAccessActionProps {
  setHelpPanelContent: (payload: DisplayHelpPanel | undefined) => void;
  setHelpPanelOpenState: (payload: boolean | true) => void;
}

export interface InheritedAccessDetailsResponse {
  data: InheritedAccessDetails[];
  isLoading: boolean;
}

const createTopLine = (fullName: string, alias: string): string => {
  return `${fullName} (${alias}@)`;
};

const createBottomLine = (teamName: string): string => {
  return `Inherited from ${teamName}`;
};

/**
 * return list of adminDetails with required topLine, bottomLine and employee alias.
 */
const getAdminAccessList = (
  inheritedAccessDetails: InheritedAccessDetails[]
): AdminDetailsObject[] => {
  return inheritedAccessDetails.map(
    ({ employeeLogin, employeeFullName, teamName }) => {
      return {
        loginAlias: employeeLogin,
        topLine: createTopLine(employeeFullName, employeeLogin),
        bottomLine: createBottomLine(teamName),
      };
    }
  );
};

function getInheritedAccess(
  inheritedAccessDetails: InheritedAccessDetails[],
  inheritedAccessActionProps: InheritedAccessActionProps,
  modalVisible: boolean,
  setModalVisible: (isVisible: boolean) => void
): JSX.Element {
  return (
    <React.Fragment>
      {inheritedAccessDetails.length > 0 && (
        <div className={styles["inherit-text"]}>
          <InheritedAccessModal
            setModalVisible={setModalVisible}
            modalVisible={modalVisible}
            adminDetails={getAdminAccessList(inheritedAccessDetails)}
          />
          <span>
            <a onClick={() => setModalVisible(true)} rel="noopener noreferrer">
              +{inheritedAccessDetails.length} admins
            </a>{" "}
            will inherit access.
          </span>
          <InfoLink
            onClick={() => {
              inheritedAccessActionProps.setHelpPanelContent({
                header: "Inherited access",
                contentType: "permissions",
                widenContent: true,
              });
              inheritedAccessActionProps.setHelpPanelOpenState(true);
            }}
          />
        </div>
      )}
    </React.Fragment>
  );
}

/**
 * Determines what to show based on the loading state of the inherited
 * access request.
 * @param inheritedAccessDetails The inherited access details (supplied by the access request).
 * @param inheritedAccessLoading The loading state of the inherited access request.
 * @param setHelpPanelContent Callback to set the help panel content.
 * @param setHelpPanelOpenState Callback to set the help panel open state.
 */
function getInheritedAccessDisplay(
  inheritedAccessDetails: InheritedAccessDetailsResponse | undefined,
  inheritedAccessLoading: LoadingState,
  inheritedAccessActionProps: InheritedAccessActionProps,
  modalVisible: boolean,
  setModalVisible: (isVisible: boolean) => void
): JSX.Element {
  switch (inheritedAccessLoading) {
    case LoadingState.LOADING: {
      return <Spinner />;
    }
    case LoadingState.RESOLVED: {
      const inheritedAccessData = inheritedAccessDetails
        ? inheritedAccessDetails.data
        : [];
      return getInheritedAccess(
        inheritedAccessData,
        inheritedAccessActionProps,
        modalVisible,
        setModalVisible
      );
    }
    default: {
      return <div />;
    }
  }
}

interface InheritedAccessProps {
  setHelpPanelContent: any;
  setHelpPanelOpenState: any;
  metrics: MetricsState;
  parentTeamId: string;
}

/**
 * Display the inherited access line only if there are any employees who will be inheriting access else don't display the inherited access.
 */
const InheritedAccess: React.FC<InheritedAccessProps> = ({
  parentTeamId,
  setHelpPanelContent,
  setHelpPanelOpenState,
  metrics,
}): JSX.Element => {
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const {
    loadingState: inheritedAccessLoadingState,
    data: inheritedAccessResponse,
    error: targetFetchError,
    sendRequest: sendInheritedAccessRequest,
  } = useHttpRequest<InheritedAccessDetailsResponse>(
    "teamAccessDetails",
    metrics,
    {
      url: `/api/v1/teams/${parentTeamId}/teamAccessDetails`,
      method: "GET",
    }
  );

  // The help panel should be loaded and be in collapsed state when the createTeam page loads.
  useEffect(() => {
    setHelpPanelContent({
      header: "Inherited access",
      contentType: "inheritedAccess",
      widenContent: true,
    });
    setHelpPanelOpenState(false);
  }, [setHelpPanelContent, setHelpPanelOpenState]);

  return getInheritedAccessDisplay(
    inheritedAccessResponse,
    inheritedAccessLoadingState,
    { setHelpPanelContent, setHelpPanelOpenState },
    modalVisible,
    setModalVisible
  );
};

const mapDispatchToProps = (
  dispatch: Dispatch
): InheritedAccessActionProps => ({
  setHelpPanelContent(helpPanelContent: DisplayHelpPanel | undefined) {
    dispatch(helpPanelSlice.actions.setHelpPanelContent(helpPanelContent));
  },
  setHelpPanelOpenState(helpPanelState: boolean) {
    dispatch(helpPanelSlice.actions.setHelpPanelOpenState(helpPanelState));
  },
});

export default connect(null, mapDispatchToProps)(InheritedAccess);
